import type { DatabaseDriver, DriverCallContext } from '@w/db'
import { classify, isCustomTable, CustomSqlError, type ClassifyContext, type ColumnRow, type Kind, type SchemaRow } from './classify'
import { expand, newId, resolvedTypeExpansion, type Statement } from './expand'
import { withLocalIndexes } from './indexes'
import { fold, quoteIdent, validName } from './names'
import { assignmentColumns, bindParams, indexExpression, keyword, mentions, splitStatements, tableColumns, tokenize } from './lexer'

export interface RunMeta { pageId: string }
/** T7 persists executable restoration through tx, atomically with the user's commit.
 * Contract: decisions “Undo” and “Schema changes are captured by the driver”.
 * No local undo table and no before-image in __cdb_ddl_log.
 */
export interface RecoveryProgramStatement extends Statement { meta?: RunMeta }
export interface RecoveryPayload {
  tableName: string
  pageId?: string
  program: readonly RecoveryProgramStatement[]
  beforeImages: readonly Statement[]
}
export interface Recovery { cdbPreCommit(tx: DatabaseDriver, payload: RecoveryPayload): Promise<void> }
export interface CustomDriver extends DatabaseDriver {
  createDatabase(input: { table: string; spaceId?: string }): Promise<void>
  createDatabaseRow(input: DatabaseRowInput): Promise<void>
  listDatabases(): Promise<{ name: string }[]>
  run(statements: readonly Statement[], meta?: RunMeta): Promise<void>
  reapply(program: readonly RecoveryProgramStatement[], complete: (tx: DatabaseDriver) => Promise<void>): Promise<void>
  transaction<T>(fn: (tx: CustomDriver) => Promise<T>, ctx?: DriverCallContext): Promise<T>
  subscribe(sql: string, params: unknown[], onInvalidate: () => void): () => void
}
export interface DatabaseRowInput { id: string; spaceId: string; userId: string; table: string; title: string; now: number; data: Uint8Array; values?: Record<string, unknown> }
export interface ReactiveInner extends DatabaseDriver {
  subscribe?(sql: string, onInvalidate: () => void): Promise<() => void> | (() => void)
}
export interface CustomDriverOptions { appTables: ReadonlySet<string> | readonly string[]; recovery: Recovery; defaultSpaceId?: string }
interface TransactionState {
  resourceScope?: string
  table?: string
  ownedPages: Set<string>
  writes: number
  pageDdl: number
  pageRows: number
  meta?: RunMeta
  program: RecoveryProgramStatement[]
  beforeImages: Statement[]
  captured: Set<string>
  typeName?: string
  destinationTable?: string
  deletedLinks: { pageId: string; target: unknown; statement: Statement }[]
  pageFields: { pageId: string; columns: string[]; values: unknown[]; statement: Statement }[]
}
const createRowSql = 'INSERT INTO "pages" ("id", "kind", "title", "titleKey", "inheritance", "updatedAt", "data", "editedBy") VALUES (?, \'databaseRow\', ?, ?, \'inherit\', ?, ?, ?)'
const createRelationSql = 'INSERT INTO "pageRelations" ("fromPageId", "toPageId", "type") VALUES (?, ?, \'child\')'

/** Called inside the existing permanent page-delete transaction. */
export async function purgeDatabaseRow(tx: DatabaseDriver, id: string, appTables: ReadonlySet<string> | readonly string[]): Promise<void> {
  const tables = await tx.query<{ name: string }>("SELECT name FROM sqlite_schema WHERE type='table'")
  for (const { name } of tables) {
    if (!isCustomTable(name, appTables)) continue
    const columns = await tx.query<ColumnRow>(`PRAGMA table_xinfo(${quoteIdent(name)})`)
    if (columns.some(column => fold(column.name) === 'id')) await tx.exec(`DELETE FROM ${quoteIdent(name)} WHERE "id"=?`, [id])
  }
}

/** One owner holds the connection until capture restoration/readback completes.
 * schema_after = atomic(expanded(schema_before)); log_after = log_before + DDL.
 * On failure both equal before; capture_after = full for either outcome.
 */
export function createCustomDriver(inner: ReactiveInner, options: CustomDriverOptions): CustomDriver {
  // INV-read: reads/subscriptions belong to the inner driver; no custom write
  // transaction, capture toggle or schema introspection (driver §1).
  function readOnly(sql: string): boolean {
    const kind = classify(sql, { appTables: options.appTables })
    return !('code' in kind) && kind.type === 'read'
  }
  let queue: Promise<unknown> = Promise.resolve()
  function enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const result = queue.then(fn)
    queue = result.catch(() => {})
    return result
  }
  // Capture is per-connection state (P67: set at open). Transactions run one at a time on the
  // inner connection, and only this driver's own toggles turn it off, so it is set on first use
  // and restored with readback only by a transaction that toggled it (SQLite practice:
  // per-connection PRAGMA once at open). captureOff: the last toggle may have left it off.
  let captureSet = false
  let captureOff = false
  let toggled = false
  async function setCapture(tx: DatabaseDriver, mode: 'off' | 'full'): Promise<void> {
    if (mode === 'off') { captureOff = true; toggled = true }
    await tx.exec(`PRAGMA capture_data_changes_conn('${mode}')`)
    if (mode === 'full') captureOff = false
  }
  async function restoreCapture(tx: DatabaseDriver): Promise<void> {
    if (!toggled && !captureOff) return
    await setCapture(tx, 'full')
    const mode = await tx.query<{ mode: string }>('PRAGMA capture_data_changes_conn')
    if (mode.length !== 1 || mode[0].mode !== 'full') throw new Error('Custom driver defect: capture is not full before releasing the connection')
  }
  function ownedTransaction<T>(fn: (tx: CustomDriver) => Promise<T>, ctx?: DriverCallContext, meta?: RunMeta, bootstrap?: (tx: DatabaseDriver, state: TransactionState) => Promise<void>, complete?: (tx: DatabaseDriver) => Promise<void>): Promise<T> {
    // The inner workspace runner owns admission, just as it does for app writes.
    // Restoration/readback must finish before COMMIT releases its connection.
    const exclusiveContext = { ...ctx, exclusive: true }
    return inner.transaction(async tx => {
      toggled = false
      if (!captureSet) { await setCapture(tx, 'full'); captureSet = true }
      const [captureStart] = await tx.query<{ id: string | null }>('SELECT CAST(MAX(change_id) AS TEXT) AS id FROM turso_cdc')
      const state: TransactionState = { ownedPages: new Set(), writes: 0, pageDdl: 0, pageRows: 0, meta, program: [], beforeImages: [], captured: new Set(), deletedLinks: [], pageFields: [] }
      let value: T
      try {
        await bootstrap?.(tx, state)
        value = await fn(scoped(tx, state))
        if (state.pageRows > 0 && state.pageDdl !== 1) throw new CustomSqlError('E_MIXED_PAGE_DDL', 'Page writes must belong to one page-coupled DDL')
        const touched = await tx.query<{ table_name: string }>('SELECT DISTINCT table_name FROM turso_cdc WHERE change_id>? AND table_name IS NOT NULL AND change_type<>2', [captureStart?.id ? BigInt(captureStart.id) : -1])
        const permitted = new Set([state.table, state.destinationTable, '__cdb_ddl_log', ...((state.pageDdl > 0 && state.meta) || state.ownedPages.size > 0 ? ['pages', 'pagePermissions', 'pageRelations'] : [])].filter((name): name is string => !!name).map(name => fold(name)))
        if (state.program.length > 0 && touched.some(row => !permitted.has(fold(row.table_name)))) throw new CustomSqlError('E_MIXED_DATABASES', 'Custom transaction effects cannot write another database')
        for (const link of state.deletedLinks) {
          const [stillPresent] = await tx.query('SELECT 1 FROM "pageRelations" WHERE "fromPageId"=? AND "toPageId"=? AND "type"=\'link\'', [link.pageId, link.target])
          if (stillPresent) state.beforeImages = state.beforeImages.filter(statement => statement !== link.statement)
        }
        for (const image of state.pageFields) {
          const [current] = await snapshotRows(tx, 'pages', image.columns, { column: 'id', value: image.pageId })
          if (!current) continue
          const changed = image.columns.map((name, index) => ({ name, value: image.values[index], changed: !snapshotEqual(image.values[index], current[index]) })).filter(field => field.changed)
          const replacement: Statement = { sql: `UPDATE "pages" SET ${changed.map(field => `${quoteIdent(field.name)}=?`).join(', ')} WHERE "id"=?`, params: [...changed.map(field => field.value), image.pageId] }
          state.beforeImages = state.beforeImages.flatMap(statement => statement === image.statement ? (changed.length > 0 ? [replacement] : []) : [statement])
        }
        await complete?.(tx)
        if (state.program.length > 0) await options.recovery.cdbPreCommit(tx, {
          tableName: state.destinationTable ?? state.table ?? state.typeName ?? '', ...((state.resourceScope ?? state.meta?.pageId) === undefined ? {} : { pageId: state.resourceScope ?? state.meta?.pageId }),
          program: state.program, beforeImages: state.beforeImages,
        })
      } catch (error) {
        try { await restoreCapture(tx) }
        catch (restoreError) { throw new SuppressedError(restoreError, error, 'Custom driver capture restoration failed') }
        throw error
      }
      await restoreCapture(tx)
      return value
    }, exclusiveContext)
  }
  async function schema(tx: DatabaseDriver): Promise<SchemaRow[]> {
    const rows = await tx.query<SchemaRow>('SELECT type, name, tbl_name, sql FROM sqlite_schema')
    if (rows.some(row => row.name === '__turso_internal_types')) {
      const types = await tx.query<{ name: string; sql: string }>('SELECT name, sql FROM __turso_internal_types')
      rows.push(...types.map(type => {
        const name = tokenize(type.sql)[2]?.value ?? type.name
        return { type: 'type', name, tbl_name: name, sql: type.sql }
      }))
    }
    return rows
  }
  async function classifyInTransaction(tx: DatabaseDriver, sql: string, state: TransactionState): Promise<{ kind: Kind; context: ClassifyContext }> {
    const schemaRows = await schema(tx)
    const base: ClassifyContext = { appTables: options.appTables, schemaRows, pageId: state.meta?.pageId, pageRowsOnly: !!state.meta }
    const initial = classify(sql, base)
    if ('code' in initial) throw new CustomSqlError(initial.code, initial.message)
    let columns: ColumnRow[] = []
    const ftsColumns: string[] = []
    let dropReferences = false
    let referenced = false
    if (initial.table) {
      columns = await tx.query<ColumnRow>(`PRAGMA table_xinfo(${quoteIdent(initial.table)})`)
      const definition = schemaRows.find(row => row.type === 'table' && fold(row.name) === fold(initial.table ?? ''))
      if (definition?.sql) {
        const declared = tableColumns(definition.sql)
        columns = columns.map(column => ({ name: column.name, type: declared.find(item => fold(item.name) === fold(column.name))?.type ?? column.type, hidden: column.hidden, pk: column.pk }))
      }
      const foreignKeys = await tx.query<{ from: string }>(`PRAGMA foreign_key_list(${quoteIdent(initial.table)})`)
      dropReferences = foreignKeys.some(key => fold(key.from) === fold(initial.column ?? ''))
      for (const row of schemaRows) {
        if (row.type === 'index' && fold(row.tbl_name) === fold(initial.table) && row.sql && tokenize(row.sql).some(token => keyword(token, 'USING'))) {
          for (const column of columns) if (mentions(indexExpression(row.sql), column.name)) ftsColumns.push(column.name)
        }
        if (row.type === 'table' && row.sql && tokenize(row.sql).some((token, i, tokens) => keyword(token, 'REFERENCES') && fold(tokens[i + 1]?.value ?? '') === fold(initial.table ?? ''))) referenced = true
      }
    }
    const [foreignKeyMode] = await tx.query<{ foreign_keys: number }>('PRAGMA foreign_keys')
    const enumTypes = schemaRows.filter(row => row.type === 'type')
    const typeTables: string[] = []
    if (initial.type === 'type') {
      for (const row of schemaRows) {
        if (row.type !== 'table') continue
        const typeColumns = await tx.query<ColumnRow>(`PRAGMA table_xinfo(${quoteIdent(row.name)})`)
        if (typeColumns.some(column => fold(column.type.replace(/\[\]\s*$/u, '').trim()) === fold(initial.name ?? ''))) typeTables.push(row.name)
      }
    }
    const enumArrayColumns = columns.filter(column => /\[\]\s*$/u.test(column.type) && enumTypes.some(type => fold(type.name) === fold(column.type.replace(/\[\]\s*$/u, '').trim()) && tokenize(type.sql ?? '').some(token => keyword(token, 'ENUM')))).map(column => column.name)
    const context: ClassifyContext = { ...base, columns, ftsColumns, enumArrayColumns, dropReferences, referenced, foreignKeysEnabled: foreignKeyMode?.foreign_keys === 1, typeTables }
    const kind = classify(sql, context)
    if ('code' in kind) throw new CustomSqlError(kind.code, kind.message)
    return { kind, context }
  }
  function account(kind: Kind, state: TransactionState, context: ClassifyContext): void {
    if (kind.type === 'read') return
    const page = kind.type === 'dml' && fold(kind.table ?? '') === 'pages' && !!state.meta
    if (page) {
      if (state.writes > state.pageDdl + state.pageRows) throw new CustomSqlError('E_MIXED_DATABASES', 'Page rows cannot mix with custom rows')
      state.pageRows++
    } else {
      const typeTable = kind.type === 'type' ? context.typeTables?.[0] : undefined
      const table = kind.table ?? typeTable ?? state.table
      if (table && state.table && fold(table) !== fold(state.table)) throw new CustomSqlError('E_MIXED_DATABASES', 'One database per transaction')
      if (table) state.table = table
      if (kind.type === 'table' && kind.action === 'rename') state.destinationTable = kind.newName
      if (kind.pageCoupled) {
        if (state.pageDdl > 0 || state.writes > state.pageRows) throw new CustomSqlError('E_MIXED_PAGE_DDL', 'Page-coupled DDL must run alone')
        state.pageDdl++
      } else if (state.pageDdl > 0 || state.pageRows > 0) {
        throw new CustomSqlError('E_MIXED_PAGE_DDL', 'Page-coupled DDL cannot mix with other work')
      }
    }
    state.writes++
  }
  async function tableImage(tx: DatabaseDriver, tableName: string, schemaRows: readonly SchemaRow[]): Promise<{ drop: Statement[]; restore: Statement[] }> {
    const definition = schemaRows.find(row => row.type === 'table' && fold(row.name) === fold(tableName))
    const views = schemaRows.filter(row => row.type === 'view' && row.sql && mentions(row.sql, tableName))
    const drop: Statement[] = [...views.map(row => ({ sql: `DROP VIEW IF EXISTS ${quoteIdent(row.name)}` })), { sql: `DROP TABLE IF EXISTS ${quoteIdent(tableName)}` }]
    if (!definition?.sql) return { drop, restore: [] }
    const columns = await tx.query<ColumnRow>(`PRAGMA table_xinfo(${quoteIdent(tableName)})`)
    const writable = columns.filter(column => column.hidden === 0)
    const alias = ['rowid', '_rowid_', 'oid'].find(name => !columns.some(column => fold(column.name) === name))
    if (!alias) throw new CustomSqlError('E_TABLE_KIND', 'All rowid aliases are shadowed')
    const selection = [alias, ...writable.map(column => column.name)].map(name => quoteIdent(name)).join(', ')
    const records = await snapshotRows(tx, tableName, [alias, ...writable.map(column => column.name)])
    const restore: Statement[] = [{ sql: definition.sql }, ...records.map(record => ({
      sql: `INSERT INTO ${quoteIdent(tableName)} (${selection}) VALUES (${[alias, ...writable].map(() => '?').join(', ')})`,
      params: record,
    }))]
    if (tokenize(definition.sql).some(token => keyword(token, 'AUTOINCREMENT'))) {
      const [sequence] = await tx.query<{ seq: string }>('SELECT CAST(seq AS TEXT) AS seq FROM sqlite_sequence WHERE name=?', [tableName])
      restore.push({ sql: 'DELETE FROM sqlite_sequence WHERE name=?', params: [tableName] })
      if (sequence) restore.push({ sql: 'INSERT INTO sqlite_sequence(name,seq) VALUES (?,?)', params: [tableName, exactInteger(sequence.seq)] })
    }
    for (const row of schemaRows) if (row.sql && fold(row.tbl_name) === fold(tableName) && (row.type === 'index' || row.type === 'trigger')) restore.push({ sql: row.sql })
    restore.push(...views.filter((row): row is SchemaRow & { sql: string } => row.sql !== null).map(row => ({ sql: row.sql })))
    return { drop, restore }
  }
  function exactInteger(value: string): number | bigint {
    const integer = BigInt(value)
    const number = Number(integer)
    return Number.isSafeInteger(number) ? number : integer
  }
  function snapshotEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) return true
    if (left instanceof Uint8Array && right instanceof Uint8Array) return left.length === right.length && left.every((byte, index) => byte === right[index])
    return false
  }
  function restoreImages(images: readonly { restore: Statement[] }[]): Statement[] {
    const statements = images.flatMap(image => image.restore)
    const kind = (statement: Statement) => {
      const tokens = tokenize(statement.sql)
      if (keyword(tokens[0], 'CREATE')) return (keyword(tokens[1], 'UNIQUE') ? tokens[2] : tokens[1])?.value.toUpperCase()
      return null
    }
    const dependencies = statements.filter(statement => ['INDEX', 'TRIGGER', 'VIEW'].includes(kind(statement) ?? ''))
    const seen = new Set<string>()
    return [
      ...statements.filter(statement => kind(statement) === 'TABLE'),
      ...statements.filter(statement => !kind(statement)),
      ...dependencies.filter(statement => { if (seen.has(statement.sql)) return false; seen.add(statement.sql); return true }),
    ]
  }
  async function snapshotRows(tx: DatabaseDriver, tableName: string, columns: readonly string[], where?: { column: string; value: string; extra?: string }): Promise<unknown[][]> {
    // Native row materialization converts SQLite INTEGER to Number. Select its
    // decimal text before crossing that boundary, even for INTEGER held in ANY.
    const selection = columns.flatMap((name, index) => [
      `CASE WHEN typeof(${quoteIdent(name)})='integer' THEN CAST(${quoteIdent(name)} AS TEXT) ELSE ${quoteIdent(name)} END AS ${quoteIdent(`value_${index}`)}`,
      `typeof(${quoteIdent(name)}) AS ${quoteIdent(`type_${index}`)}`,
    ]).join(', ')
    const records = await tx.query<Record<string, unknown>>(`SELECT ${selection} FROM ${quoteIdent(tableName)}${where ? ` WHERE ${quoteIdent(where.column)}=?${where.extra ?? ''}` : ''}`, where ? [where.value] : undefined)
    return records.map(record => columns.map((_name, index) => record[`type_${index}`] === 'integer' ? exactInteger(String(record[`value_${index}`])) : record[`value_${index}`]))
  }
  async function pageImage(tx: DatabaseDriver, tableName: string, column: string, pageId: string, state: TransactionState): Promise<void> {
    const key = `page:${tableName}:${column}:${pageId}`
    if (state.captured.has(key)) return
    const columns = (await tx.query<ColumnRow>(`PRAGMA table_xinfo(${quoteIdent(tableName)})`)).filter(item => item.hidden === 0)
    const alias = ['rowid', '_rowid_', 'oid'].find(name => !columns.some(item => fold(item.name) === name))
    if (!alias) throw new CustomSqlError('E_TABLE_KIND', 'All rowid aliases are shadowed')
    const names = [alias, ...columns.map(item => item.name)]
    const rows = await snapshotRows(tx, tableName, names, { column, value: pageId })
    const undo: Statement[] = [...(rows.length === 0 ? [{ sql: `DELETE FROM ${quoteIdent(tableName)} WHERE ${quoteIdent(column)}=?`, params: [pageId] }] : []), ...rows.map(row => ({
      sql: `INSERT INTO ${quoteIdent(tableName)} (${names.map(name => quoteIdent(name)).join(', ')}) VALUES (${names.map(() => '?').join(', ')}) ON CONFLICT DO UPDATE SET ${columns.map(item => `${quoteIdent(item.name)}=excluded.${quoteIdent(item.name)}`).join(', ')}`, params: row,
    }))]
    state.beforeImages = [...undo, ...state.beforeImages]
    state.captured.add(key)
  }
  async function pageUpdateImage(tx: DatabaseDriver, sql: string, pageId: string, state: TransactionState): Promise<void> {
    const columns = assignmentColumns(sql).filter(name => fold(name) !== 'id' && !state.captured.has(`page-field:${pageId}:${fold(name)}`))
    if (columns.length === 0) return
    const [values] = await snapshotRows(tx, 'pages', columns, { column: 'id', value: pageId })
    if (!values) return
    const statement = { sql: `UPDATE "pages" SET ${columns.map(name => `${quoteIdent(name)}=?`).join(', ')} WHERE "id"=?`, params: [...values, pageId] }
    state.beforeImages.unshift(statement)
    state.pageFields.push({ pageId, columns, values, statement })
    for (const name of columns) state.captured.add(`page-field:${pageId}:${fold(name)}`)
  }
  async function deletedPageLinksImage(tx: DatabaseDriver, pageId: string, state: TransactionState): Promise<void> {
    const key = `page-deleted-links:${pageId}`
    if (state.captured.has(key)) return
    const columns = (await tx.query<ColumnRow>('PRAGMA table_xinfo("pageRelations")')).filter(item => item.hidden === 0)
    const names = columns.map(item => item.name)
    const rows = await snapshotRows(tx, 'pageRelations', names, { column: 'fromPageId', value: pageId, extra: ' AND "type"=\'link\'' })
    const statements = rows.map(row => ({
      sql: `INSERT INTO "pageRelations" (${names.map(name => quoteIdent(name)).join(', ')}) VALUES (${names.map(() => '?').join(', ')}) ON CONFLICT DO NOTHING`, params: row,
    }))
    state.beforeImages.unshift(...statements)
    state.deletedLinks.push(...statements.map((statement, index) => ({ pageId, target: rows[index][names.indexOf('toPageId')], statement })))
    state.captured.add(key)
  }
  async function affectedTables(tx: DatabaseDriver, tableName: string, schemaRows: readonly SchemaRow[], foreignKeysEnabled: boolean, scanRootTriggers = true, foreignKeyOperation: 'delete' | 'update' = 'delete'): Promise<string[]> {
    const tables = schemaRows.filter(row => row.type === 'table')
    const affected = [tableName]
    for (let at = 0; at < affected.length; at++) {
      const current = affected[at]
      for (const trigger of schemaRows.filter(row => row.type === 'trigger' && fold(row.tbl_name) === fold(current) && (at > 0 || scanRootTriggers))) {
        const tokens = tokenize(trigger.sql ?? '')
        for (let index = 0; index < tokens.length; index++) {
          let name: string | undefined
          if (keyword(tokens[index], 'INTO') && tokens.slice(Math.max(0, index - 3), index).some(token => keyword(token, 'INSERT') || keyword(token, 'REPLACE'))) name = tokens[index + 1]?.value
          if (keyword(tokens[index], 'UPDATE')) name = keyword(tokens[index + 1], 'OR') ? tokens[index + 3]?.value : tokens[index + 1]?.value
          if (keyword(tokens[index], 'DELETE') && keyword(tokens[index + 1], 'FROM')) name = tokens[index + 2]?.value
          const target = tables.find(row => fold(row.name) === fold(name ?? ''))
          if (target && !affected.some(item => fold(item) === fold(target.name))) affected.push(target.name)
        }
      }
      if (foreignKeysEnabled) for (const table of tables) {
        if (affected.some(item => fold(item) === fold(table.name))) continue
        const keys = await tx.query<{ table: string; on_delete: string; on_update: string }>(`PRAGMA foreign_key_list(${quoteIdent(table.name)})`)
        if (keys.some(key => fold(key.table) === fold(current) && ['CASCADE', 'SET NULL', 'SET DEFAULT'].includes((foreignKeyOperation === 'delete' ? key.on_delete : key.on_update).toUpperCase()))) affected.push(table.name)
      }
    }
    return affected
  }
  async function captureBefore(tx: DatabaseDriver, kind: Kind, context: ClassifyContext, state: TransactionState): Promise<void> {
    if (kind.type === 'read' || kind.type === 'index') return
    if (kind.type === 'dml' && fold(kind.table ?? '') === 'pages' && state.meta) {
      if (keyword(tokenize(kind.sql)[0], 'UPDATE')) {
        if (assignmentColumns(kind.sql).some(name => fold(name) === 'deletedat') && context.schemaRows?.some(row => row.type === 'table' && fold(row.name) === fold('pageRelations'))) await deletedPageLinksImage(tx, state.meta.pageId, state)
        await pageUpdateImage(tx, kind.sql, state.meta.pageId, state)
      } else {
        await pageImage(tx, 'pages', 'id', state.meta.pageId, state)
      }
      return
    }
    const key = `${kind.type === 'type' ? 'type' : 'table'}:${fold(kind.table ?? kind.name ?? '')}`
    if (state.captured.has(key)) return
    const schemaRows = context.schemaRows ?? []
    let undo: Statement[]
    if (kind.type === 'type') {
      state.typeName = kind.name
      if (tokenize(kind.sql).some(token => keyword(token, 'DROP'))) for (const tableName of context.typeTables ?? []) {
        const affected = await affectedTables(tx, tableName, schemaRows, context.foreignKeysEnabled ?? false, true, 'update')
        if (affected.some(table => fold(table) !== fold(tableName))) throw new CustomSqlError('E_MIXED_DATABASES', 'Custom enum effects cannot write another database')
      }
      const images = await Promise.all((context.typeTables ?? []).map(tableName => tableImage(tx, tableName, schemaRows)))
      const definition = schemaRows.find(row => row.type === 'type' && fold(row.name) === fold(kind.name ?? ''))
      // The enum catalog retains the CREATE identifier spelling, including
      // quoting. DROP must address that exact persisted spelling.
      const typeToken = tokenize(definition?.sql ?? kind.sql)[2]?.raw ?? quoteIdent(kind.name ?? '')
      undo = [...images.toReversed().flatMap(image => image.drop), { sql: `DROP TYPE IF EXISTS ${typeToken}` }, ...(definition?.sql ? [{ sql: definition.sql }] : []), ...restoreImages(images)]
    } else {
      if (kind.type === 'table' && kind.action === 'drop') {
        const tables = await affectedTables(tx, kind.table ?? '', schemaRows, context.foreignKeysEnabled ?? false, false)
        if (tables.some(tableName => fold(tableName) !== fold(kind.table ?? ''))) throw new CustomSqlError('E_MIXED_DATABASES', 'Custom schema recovery cannot affect another database')
      }
      const image = await tableImage(tx, kind.table ?? '', schemaRows)
      undo = [...(kind.type === 'table' && kind.action === 'rename' ? [{ sql: `DROP TABLE IF EXISTS ${quoteIdent(kind.newName ?? '')}` }] : []), ...image.drop, ...image.restore]
    }
    state.beforeImages = [...undo, ...state.beforeImages]
    state.captured.add(key)
  }
  async function log(tx: DatabaseDriver, kind: Kind, expanded: readonly Statement[], state: TransactionState): Promise<void> {
    // G1: table rename carriers identify the destination; column newName is not a table.
    const tableName = kind.type === 'table' && kind.action === 'rename' ? kind.newName : kind.table ?? kind.name
    const [previous] = await tx.query<{ pageId: string }>('SELECT pageId FROM __cdb_ddl_log WHERE tableName=? COLLATE NOCASE ORDER BY createdAt DESC,id DESC LIMIT 1', [kind.table ?? kind.name ?? ''])
    state.resourceScope ??= state.meta?.pageId ?? previous?.pageId ?? options.defaultSpaceId
    const id = newId()
    await tx.exec('INSERT INTO "__cdb_ddl_log" (id, pageId, tableName, original, expanded, createdAt) VALUES (?, ?, ?, ?, ?, ?)',
      [id, state.resourceScope ?? '', tableName ?? '', kind.sql, JSON.stringify(expanded), Date.now()])
    state.beforeImages.unshift({ sql: 'DELETE FROM "__cdb_ddl_log" WHERE id=?', params: [id] })
  }
  async function execute<T>(tx: DatabaseDriver, statement: Statement, state: TransactionState, query: boolean): Promise<T[]> {
    if (readOnly(statement.sql)) {
      if (query) return tx.query<T>(statement.sql, statement.params)
      await tx.exec(statement.sql, statement.params)
      return []
    }
    const ddl = ['CREATE', 'ALTER', 'DROP'].some(word => keyword(tokenize(statement.sql)[0], word))
    let executableSql = statement.sql
    if (ddl) {
      try { executableSql = bindParams(statement.sql, statement.params) }
      catch (error) { throw new CustomSqlError('E_SQL', String(error)) }
    }
    const { kind, context } = await classifyInTransaction(tx, executableSql, state)
    const expandedKind = { ...kind }
    kind.sql = statement.sql
    account(kind, state, context)
    const rowPages = kind.table && context.columns?.some(column => fold(column.name) === 'id') && context.schemaRows?.some(row => row.type === 'table' && fold(row.name) === 'pages' && row.sql && tableColumns(row.sql).some(column => fold(column.name) === 'kind'))
    if (rowPages && kind.type === 'dml' && kind.action === 'delete') {
      const tokens = tokenize(statement.sql)
      const from = tokens.findIndex(token => keyword(token, 'FROM') && token.depth === 0)
      const returning = tokens.findIndex(token => keyword(token, 'RETURNING') && token.depth === 0)
      const end = returning >= 0 ? tokens[returning].start : tokens.at(-1)?.value === ';' ? tokens.at(-1)?.start : statement.sql.length
      const source = statement.sql.slice(tokens[from].end, end)
      const records = await tx.query<Record<string, unknown>>(`SELECT * FROM ${source}`, statement.params)
      const owned: Record<string, unknown>[] = []
      for (const record of records) {
        const [page] = await tx.query<{ id: string }>('SELECT id FROM pages WHERE id=? AND kind=\'databaseRow\'', [record.id])
        if (!page) continue
        owned.push(record)
        state.ownedPages.add(page.id)
        await pageUpdateImage(tx, 'UPDATE pages SET deletedAt=?,deletedBy=?,updatedAt=? WHERE id=?', page.id, state)
        if (context.schemaRows?.some(row => row.name === 'pageRelations')) await deletedPageLinksImage(tx, page.id, state)
        const now = Date.now()
        await tx.exec('UPDATE pages SET deletedAt=?,deletedBy=editedBy,updatedAt=? WHERE id=? AND deletedAt IS NULL', [now, now, page.id])
      }
      if (owned.length > 0 || records.length === 0) {
        state.program.push({ sql: statement.sql, ...(statement.params ? { params: statement.params } : {}) })
        if (!query || returning < 0) return []
        const selection = statement.sql.slice(tokens[returning].end).replace(/;\s*$/u, '')
        return tx.query<T>(`SELECT ${selection} FROM ${quoteIdent(kind.table ?? '')} WHERE id IN (${owned.map(() => '?').join(',') || 'NULL'})`, owned.map(record => record.id))
      }
    }
    await captureBefore(tx, kind, context, state)
    if (rowPages && kind.type === 'table' && kind.action === 'drop') {
      const rows = await tx.query<{ id: string }>(`SELECT p.id FROM pages p JOIN ${quoteIdent(kind.table ?? '')} t ON p.id=t.id WHERE p.kind='databaseRow'`)
      for (const { id } of rows) {
        state.ownedPages.add(id)
        await pageImage(tx, 'pages', 'id', id, state)
        if (context.schemaRows?.some(row => row.name === 'pageRelations')) {
          await pageImage(tx, 'pageRelations', 'toPageId', id, state)
          await pageImage(tx, 'pageRelations', 'fromPageId', id, state)
          await tx.exec('DELETE FROM pageRelations WHERE fromPageId=? OR toPageId=?', [id, id])
        }
        if (context.schemaRows?.some(row => row.name === 'pagePermissions')) { await pageImage(tx, 'pagePermissions', 'pageId', id, state); await tx.exec('DELETE FROM pagePermissions WHERE pageId=?', [id]) }
        await tx.exec('DELETE FROM pages WHERE id=?', [id])
      }
    }
    if (kind.type !== 'index') state.program.push({ sql: statement.sql, ...(statement.params ? { params: statement.params } : {}), ...(state.meta ? { meta: { ...state.meta } } : {}) })
    if (kind.type === 'read' || kind.type === 'dml') {
      if (kind.type === 'dml' && fold(kind.table ?? '') === 'pages' && state.meta) {
        const tokens = tokenize(statement.sql)
        // RETURNING checks actual affected keys. Forbid changing a page's identity:
        // otherwise an UPDATE could move an unrelated source page into meta.pageId.
        if (assignmentColumns(statement.sql).some(column => fold(column) === 'id')) throw new CustomSqlError('E_APP_TABLE', 'Page identity cannot be changed')
        const end = tokens.at(-1)?.value === ';' ? tokens.at(-1)?.start : statement.sql.length
        const returning = tokens.some(token => keyword(token, 'RETURNING') && token.depth === 0)
        const checked = `${statement.sql.slice(0, end)}${returning ? ',' : ' RETURNING'} "id" AS "__cdb_checked_page"`
        const rows = await tx.query<Record<string, unknown>>(checked, statement.params)
        if (rows.some(row => row.__cdb_checked_page !== state.meta?.pageId)) throw new CustomSqlError('E_APP_TABLE', 'Page writes must target meta.pageId')
        return rows.map(({ __cdb_checked_page: _page, ...row }) => row as T)
      }
      if (query) return tx.query<T>(statement.sql, statement.params)
      await tx.exec(statement.sql, statement.params)
      return []
    }
    if (kind.type === 'index') {
      await tx.exec(statement.sql, statement.params)
      return []
    }
    if (kind.type === 'type') {
      // INV-type: one uncaptured execution allocates identities; read back the
      // resolved form, then log at this position (I13; type statements paragraph).
      await setCapture(tx, 'off')
      await tx.exec(executableSql)
      let statements: Statement[] = [{ sql: executableSql }]
      if (kind.action !== 'drop') {
        const [resolved] = await tx.query<{ sql: string }>('SELECT sql FROM __turso_internal_types WHERE name=? COLLATE NOCASE OR name=? COLLATE NOCASE', [kind.name, quoteIdent(kind.name ?? '')])
        if (!resolved?.sql) throw new CustomSqlError('E_SQL', 'Engine did not expose a resolved type definition')
        statements = resolvedTypeExpansion(expandedKind, resolved.sql)
      }
      await setCapture(tx, 'full')
      await log(tx, kind, statements, state)
      return []
    }
    const statements = expand(expandedKind, context.schemaRows ?? [], { columns: context.columns })
    await log(tx, kind, statements, state)
    await setCapture(tx, 'off')
    await withLocalIndexes(tx, statements, async () => {
      for (const expanded of statements) await tx.exec(expanded.sql, expanded.params)
    })
    // sqlite_sequence is local bookkeeping, never a replay statement or captured row.
    if (kind.rebuild && context.schemaRows?.some(row => row.type === 'table' && fold(row.name) === fold(kind.table ?? '') && row.sql && tokenize(row.sql).some(token => keyword(token, 'AUTOINCREMENT')))) {
      const create = tokenize(statements.find(statement => keyword(tokenize(statement.sql)[0], 'CREATE') && keyword(tokenize(statement.sql)[1], 'TABLE'))?.sql ?? '')
      await tx.exec('UPDATE sqlite_sequence SET name=? WHERE name=?', [kind.table, create[2]?.value])
    }
    await setCapture(tx, 'full')
    return []
  }
  async function atomic<T>(tx: DatabaseDriver, state: TransactionState, fn: (nested: DatabaseDriver, nextState: TransactionState) => Promise<T>, ctx?: DriverCallContext): Promise<T> {
    const nextState = { ...state, ownedPages: new Set(state.ownedPages), program: [...state.program], beforeImages: [...state.beforeImages], captured: new Set(state.captured), deletedLinks: [...state.deletedLinks], pageFields: [...state.pageFields] }
    let result: T
    try { result = await tx.transaction(nested => fn(nested, nextState), ctx) }
    catch (error) {
      try { if (captureOff) await setCapture(tx, 'full') }
      catch (restoreError) { throw new SuppressedError(restoreError, error, 'Custom driver nested capture restoration failed') }
      throw error
    }
    if (captureOff) await setCapture(tx, 'full')
    Object.assign(state, nextState)
    return result
  }
  function scoped(tx: DatabaseDriver, state: TransactionState): CustomDriver {
    const driver: CustomDriver = {
      createDatabase: () => Promise.reject(new CustomSqlError('E_MIXED_PAGE_DDL', 'Create a database outside another transaction')),
      createDatabaseRow: () => Promise.reject(new CustomSqlError('E_MIXED_PAGE_DDL', 'Create a row outside another transaction')),
      listDatabases: () => listDatabases(tx),
      reapply: () => Promise.reject(new CustomSqlError('E_MIXED_PAGE_DDL', 'Reapply outside another transaction')),
      dialect: inner.dialect,
      query<T>(sql: string, params?: unknown[], ctx?: DriverCallContext): Promise<T[]> {
        if (splitStatements(sql).length !== 1) throw new CustomSqlError('E_SQL', 'query requires one statement')
        if (readOnly(sql)) return tx.query<T>(sql, params, ctx)
        return atomic(tx, state, (nested, nextState) => execute<T>(nested, { sql, params }, nextState, true))
      },
      async exec(sql, params, ctx) {
        const statements = splitStatements(sql)
        if (statements.length > 1 && params && params.length > 0) throw new CustomSqlError('E_SQL', 'Use run for multiple parameterized statements')
        if (statements.every(statement => readOnly(statement))) {
          await tx.exec(sql, params, ctx)
          return
        }
        await atomic(tx, state, async (nested, nextState) => {
          for (const statement of statements) await execute(nested, { sql: statement, params }, nextState, false)
        })
      },
      async run(statements, meta) {
        if (statements.every(statement => readOnly(statement.sql))) {
          for (const statement of statements) await tx.exec(statement.sql, statement.params)
          return
        }
        await atomic(tx, state, async (nested, nextState) => {
          if (meta && nextState.meta && meta.pageId !== nextState.meta.pageId) throw new CustomSqlError('E_MIXED_DATABASES', 'Conflicting page metadata')
          if (meta) nextState.meta = meta
          if (nextState.resourceScope === undefined) {
            const targets = statements.flatMap(statement => {
              const kind = classify(statement.sql, { appTables: options.appTables })
              // An enum's CREATE TYPE entry is logged under its type name (log(): kind.table ?? kind.name).
              const target = 'code' in kind ? undefined : kind.table ?? (kind.type === 'type' ? kind.name : undefined)
              return target ? [target] : []
            })
            const [previous] = await nested.query<{ pageId: string }>('SELECT pageId FROM __cdb_ddl_log WHERE lower(tableName) IN (SELECT lower(value) FROM json_each(?)) ORDER BY createdAt DESC,id DESC LIMIT 1', [JSON.stringify(targets)])
            nextState.resourceScope = nextState.meta?.pageId ?? previous?.pageId ?? options.defaultSpaceId
          }
          for (const statement of statements) {
            if (splitStatements(statement.sql).length !== 1) throw new CustomSqlError('E_SQL', 'Each run item must contain one statement')
            await execute(nested, statement, nextState, false)
          }
        })
      },
      transaction: (fn, ctx) => atomic(tx, state, (nested, nextState) => fn(scoped(nested, nextState)), ctx),
      subscribe: (sql, params, onInvalidate) => subscribe(sql, params, onInvalidate),
      copyFrom: () => Promise.reject(new CustomSqlError('E_SQL', 'Use run for custom database writes')),
    }
    return driver
  }
  async function listDatabases(tx: DatabaseDriver): Promise<{ name: string }[]> {
    const rows = await tx.query<{ name: string }>("SELECT name FROM sqlite_schema WHERE type='table' ORDER BY name")
    return rows.filter(row => isCustomTable(row.name, options.appTables))
  }
  function subscribe(sql: string, params: unknown[], onInvalidate: () => void): () => void {
    const kind = classify(sql, { appTables: options.appTables })
    if ('code' in kind) throw new CustomSqlError(kind.code, kind.message)
    if (kind.type !== 'read') throw new CustomSqlError('E_SQL', 'Subscriptions require reads')
    if (!inner.subscribe) throw new Error('Inner driver does not support subscriptions')
    let bound: string
    try { bound = bindParams(sql, params) }
    catch (error) { throw new CustomSqlError('E_SQL', String(error)) }
    let closed = false
    let unsubscribe: (() => void) | undefined
    const innerSubscribe = inner.subscribe.bind(inner)
    const attach = (cleanup: () => void) => { if (closed) cleanup(); else unsubscribe = cleanup }
    const register = () => Promise.resolve(innerSubscribe(bound, () => { if (!closed) onInvalidate() })).then(cleanup => { attach(cleanup); return true }, () => false)
    // Registration stays synchronous at this boundary even when the adapter queues it, and the next owned write waits
    // for it to settle, so a write right after subscribe still invalidates the read. A failed registration (a read of a
    // table a rename or its refusal rollback just removed) is reported to that read alone, as React Query or Apollo
    // report a failed subscription to its own subscriber: onInvalidate re-runs the reader, which gets the real error. It
    // never fails an unrelated later write. The read then watches schema changes (every custom DDL and its rollback
    // write __cdb_ddl_log, log() above; flashcards database-sidebar.tsx watches the same table) and re-registers once
    // its table is back, re-running the reader again, so it never stays silently unsubscribed.
    const registration = register().then(async registered => {
      if (registered || closed) return null
      onInvalidate()
      let stopWatch: (() => void) | undefined
      let retrying = false
      let again = false
      let resubscribed = false
      // Success replaces the watch in unsubscribe (attach), so the watch stops here. A schema change during a failed
      // retry runs one more.
      const retry = async (): Promise<void> => {
        retrying = true
        again = false
        resubscribed = await register()
        retrying = false
        if (resubscribed) { if (!closed) { stopWatch?.(); onInvalidate() } }
        else if (again && !closed) await retry()
      }
      stopWatch = await innerSubscribe('SELECT id FROM "__cdb_ddl_log"', () => {
        if (closed) return
        if (retrying) again = true
        else void retry()
      })
      // A retry that won before this line keeps its registration (attach) and the watch stops.
      if (resubscribed) { stopWatch(); return null }
      attach(stopWatch)
      // List after watch (Kubernetes informers): a schema change outside the owned queue (a remote apply or a
      // replication rollback) between the failed registration and this watch is not seen by it, so check once now.
      if (retrying) again = true
      else if (!closed) await retry()
      return null
    }).catch(() => null) // A watch that cannot register leaves the read as React Query leaves a failed subscription.
    queue = queue.then(() => registration)
    return () => { closed = true; unsubscribe?.() }
  }
  return {
    reapply(program, complete) {
      const row = program[0]?.sql === createRowSql
      const count = row ? 2 : 0
      if (row && (program[1]?.sql !== createRelationSql || program[1].params?.[1] !== program[0].params?.[0])) return Promise.reject(new CustomSqlError('E_MIXED_PAGE_DDL', 'Invalid row recovery program'))
      return enqueue(() => ownedTransaction(async tx => {
        for (const statement of program.slice(count)) await tx.run([statement], statement.meta)
      }, undefined, undefined, row ? async (tx, state) => {
        const id = String(program[0].params?.[0])
        state.ownedPages.add(id)
        await pageImage(tx, 'pages', 'id', id, state)
        await pageImage(tx, 'pageRelations', 'toPageId', id, state)
        for (const statement of program.slice(0, count)) { await tx.exec(statement.sql, statement.params); state.program.push(statement) }
      } : undefined, complete))
    },
    createDatabase(input) {
      const table = input.table.normalize('NFC')
      if (!validName(table)) return Promise.reject(new CustomSqlError('E_NAME', 'Invalid database name'))
      return enqueue(() => ownedTransaction(tx => tx.run([{ sql: `CREATE TABLE ${quoteIdent(table)} ("id" TEXT PRIMARY KEY) STRICT` }]), undefined, undefined, (_tx, state) => {
        state.resourceScope = input.spaceId
        return Promise.resolve()
      }))
    },
    createDatabaseRow(input) {
      if (!isCustomTable(input.table, options.appTables)) return Promise.reject(new CustomSqlError('E_APP_TABLE', 'Row must belong to a custom table'))
      const values = Object.entries(input.values ?? {})
      if (values.some(([name]) => fold(name) === 'id')) return Promise.reject(new CustomSqlError('E_ID_COLUMN', 'Row identity is supplied separately'))
      return enqueue(() => ownedTransaction(tx => tx.run([{ sql: `INSERT INTO ${quoteIdent(input.table)} (${['id', ...values.map(([name]) => name)].map(name => quoteIdent(name)).join(', ')}) VALUES (${['id', ...values].map(() => '?').join(', ')})`, params: [input.id, ...values.map(([, value]) => value)] }]), undefined, undefined, async (tx, state) => {
        state.ownedPages.add(input.id)
        await pageImage(tx, 'pages', 'id', input.id, state)
        await pageImage(tx, 'pageRelations', 'toPageId', input.id, state)
        const statements: RecoveryProgramStatement[] = [
          { sql: createRowSql, params: [input.id, input.title, input.title.trim().toLowerCase(), input.now, input.data, input.userId] },
          { sql: createRelationSql, params: [input.spaceId, input.id] },
        ]
        for (const statement of statements) { await tx.exec(statement.sql, statement.params); state.program.push(statement) }
      }))
    },
    listDatabases: () => listDatabases(inner),
    dialect: inner.dialect,
    query: <T>(sql: string, params?: unknown[], ctx?: DriverCallContext) => readOnly(sql) ? inner.query<T>(sql, params, ctx) : enqueue(() => ownedTransaction(tx => tx.query<T>(sql, params, ctx), ctx)),
    exec: (sql, params, ctx) => splitStatements(sql).every(statement => readOnly(statement)) ? inner.exec(sql, params, ctx) : enqueue(() => ownedTransaction(tx => tx.exec(sql, params, ctx), ctx)),
    run: async (statements, meta) => {
      if (statements.every(statement => readOnly(statement.sql))) {
        for (const statement of statements) await inner.exec(statement.sql, statement.params)
        return
      }
      await enqueue(() => ownedTransaction(tx => tx.run(statements, meta), undefined, meta))
    },
    transaction: (fn, ctx) => enqueue(() => ownedTransaction(fn, ctx)),
    subscribe,
    copyFrom: () => Promise.reject(new CustomSqlError('E_SQL', 'Use run for custom database writes')),
    // queue never rejects: enqueue drops each write's failure (its caller gets it) and a subscription's registration
    // settles without rejecting (subscribe).
    close: async () => {
      await queue
      await inner.close?.()
    },
  }
}
